import { z } from "zod";

/**
 * Trip status/leg vocabulary, and the ops-map read that uses it.
 *
 * ⚠ THE BROADCAST CONTRACT THAT USED TO LIVE HERE IS GONE (0086).
 *
 * `liveTripUpdateSchema` described a `trips:live` Supabase Realtime Broadcast
 * payload with one writer and many readers. **Nothing ever wrote it.** The only
 * publisher was the client app's Dev Panel "Live bridge", deleted with the mocks
 * on 2026-08-08, and the admin ops map went on subscribing to an empty channel
 * for months while a hardcoded "demo journey" rendered in front of it. A
 * contract with no implementation is worse than no contract: it reads as a
 * working feature to everyone who finds it.
 *
 * The ops map now reads `ops_live_trips_snapshot()` through the `dispatch` Edge
 * Function. The enums stayed because eight files across the client and partner
 * apps import `LiveTripLeg` — they are the shared vocabulary for "which leg is
 * being driven", independent of how a position arrives.
 *
 * DATA WALL: nothing in this file carries a client address, name or destination
 * — and the ops payload below is the one a `hub_manager` receives, so that is a
 * rule and not a convention (rules/data-wall.md). The wall is enforced in the
 * SQL signature; this schema is the second wall, not the first.
 */
export const liveTripStatusSchema = z.enum([
  "pending",
  "awaiting_rider",
  "dispatched",
  "en_route_to_client",
  "arrived",
  "in_session",
  "completed",
  "cancelled",
]);

export type LiveTripStatus = z.infer<typeof liveTripStatusSchema>;

export const liveTripLegSchema = z.enum(["to_pickup", "to_client", "waiting", "return", "done"]);

export type LiveTripLeg = z.infer<typeof liveTripLegSchema>;

/**
 * One live trip on the operations map — the row shape of
 * `ops_live_trips_snapshot()` (0086), after the Edge layer camel-cases it.
 *
 * ONE POSITION, NEVER A HISTORY. The breadcrumb line is a separate, on-demand
 * read (`opsLiveTripTrackSchema`) because `rider_locations` is the
 * highest-volume table in the system and this shape is polled every 5 seconds.
 */
export const opsLiveTripSchema = z.object({
  tripId: z.string().uuid(),
  /** Public booking reference (e.g. "BNB-1024") — the operator's join key. */
  bookingRef: z.string().min(1),
  hubId: z.string().uuid(),
  riderId: z.string().uuid(),
  /** Staff identity is operational and not walled; the CLIENT side is. */
  riderName: z.string().min(1),
  /**
   * The booking's assigned therapist — staff identity too, not client (0252).
   * The join key for drawing a route to her known point on the `to_pickup`
   * leg only; `to_client` never gets a route to anything, because that
   * destination is the client's address (rules/data-wall.md).
   */
  therapistId: z.string().uuid().nullable(),
  status: liveTripStatusSchema,
  /** Derived from status server-side — readers never re-derive it. */
  leg: liveTripLegSchema,
  pos: z.object({
    lat: z.number().min(-90).max(90),
    lng: z.number().min(-180).max(180),
  }),
  /** When the rider's DEVICE took the fix, not when we read it. */
  recordedAt: z.string(),
  /**
   * False for a trip that has already ENDED (0251) — a frozen pin drawn while
   * `in_session`, or for up to 5 minutes after `completed`, using the rider's
   * last recorded fix before drop-off. True for an actual live position. The
   * two need different treatment on the map (a moving icon vs a static one)
   * and were indistinguishable on the wire before this.
   */
  live: z.boolean(),
  /**
   * How good the latest fix is, as the rider's phone measured it (0391).
   * Display only: the ops map ignores a vague fix instead of turning the
   * scooter toward it. Null on points from before 0391 and on phones that do
   * not report it. Defaulted so an older Edge deploy still parses.
   */
  accuracyM: z.number().min(0).nullable().default(null),
  /** The phone's course over ground, degrees from north (0391). Display only. */
  headingDeg: z.number().min(0).max(360).nullable().default(null),
  /** The phone's ground speed, m/s (0391). Display only — never mileage. */
  speedMps: z.number().min(0).nullable().default(null),
  /**
   * Version of the route the rider's phone is following (0391), or null when
   * it has published none for the live leg. A number only: the line is the
   * on-demand `opsLiveTripNavRouteSchema` read, fetched when this changes.
   * Never set on `to_client` — that line ends at the client's door.
   */
  navRouteVersion: z.number().int().positive().nullable().default(null),
});

export type OpsLiveTrip = z.infer<typeof opsLiveTripSchema>;

/** One road point, `[lng, lat]` — the GeoJSON order Mapbox draws. */
export const lngLatSchema = z.tuple([
  z.number().min(-180).max(180),
  z.number().min(-90).max(90),
]);

/** The only legs that may keep a stored route (0391, rules/data-wall.md). */
export const navRouteLegSchema = z.enum(["to_pickup", "return"]);
export type NavRouteLeg = z.infer<typeof navRouteLegSchema>;

/**
 * The rider's phone publishes the road line it is following (0391), on the
 * first route of a leg and on every reroute. Display only: rider pay is the
 * server's own route (BL-01) and never this line.
 */
export const riderNavRouteInputSchema = z
  .object({
    leg: navRouteLegSchema,
    coordinates: z.array(lngLatSchema).min(2).max(5000),
  })
  .strict();
export type RiderNavRouteInput = z.infer<typeof riderNavRouteInputSchema>;

/**
 * The rider's own route for the ops map (0391), or null when there is none
 * for the live leg. Hub-scoped like the track.
 */
export const opsLiveTripNavRouteSchema = z
  .object({
    version: z.number().int().positive(),
    leg: navRouteLegSchema,
    coordinates: z.array(lngLatSchema).min(2),
  })
  .nullable();
export type OpsLiveTripNavRoute = z.infer<typeof opsLiveTripNavRouteSchema>;

export const opsLiveTripsSnapshotSchema = z.array(opsLiveTripSchema);

/**
 * The driven path of one trip — the actual GPS breadcrumbs, oldest first.
 *
 * These are the same points `end_trip` (0017) turns into `trips.track` and
 * PostGIS bills `total_km` from, so the line an operator sees is the line that
 * was paid for. Deliberately NOT a Mapbox Directions route: a road-snapped
 * "optimal" path would hide the detour that is usually the reason someone
 * opened the map.
 *
 * Display only — a distance measured on this client is never payable
 * (SECURITY §5, money-math.md §3).
 */
export const opsLiveTripTrackSchema = z.array(
  z.object({
    lat: z.number().min(-90).max(90),
    lng: z.number().min(-180).max(180),
    recordedAt: z.string(),
  }),
);

export type OpsLiveTripTrack = z.infer<typeof opsLiveTripTrackSchema>;
